import React, { useState, useEffect, Component } from 'react';
import Layout from './components/Layout';

// ErrorBoundary — impede que crash de render desmonte o App inteiro (voltaria ao dashboard)
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  componentDidCatch(e, info) { console.error('ErrorBoundary caught:', e, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, color: '#ef4444', fontFamily: 'monospace', fontSize: 13 }}>
          <strong>Erro ao renderizar a página.</strong>
          <div style={{ marginTop: 8, opacity: 0.7 }}>{this.state.error.message}</div>
          <button
            onClick={() => this.setState({ error: null })}
            style={{ marginTop: 16, padding: '8px 16px', background: '#1f2937', border: '1px solid #374151', borderRadius: 6, color: '#f9fafb', cursor: 'pointer' }}>
            Tentar novamente
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import LoginPage from './pages/LoginPage';
import ResetSenhaPage from './pages/ResetSenhaPage';
import DashboardPage from './pages/DashboardPage';
import TitularesPage from './pages/TitularesPage';
import CartoesPage from './pages/CartoesPage';
import BancosPage from './pages/BancosPage';
import TransacoesPage from './pages/TransacoesPage';
import ConciliacaoPage from './pages/ConciliacaoPage';
import ConsultaPage from './pages/ConsultaPage';
import FechamentoFaturaPage from './pages/FechamentoFaturaPage';
import RemuneracaoPage from './pages/RemuneracaoPage';
import MeuCadastroPage from './pages/MeuCadastroPage';
import MeuPainelPage from './pages/MeuPainelPage';
import CofrePage from './pages/CofrePage';
import ConciliacaoCCPage from './pages/ConciliacaoCCPage';
import FluxoCaixaPage from './pages/FluxoCaixaPage';
import AgendaVencimentosPage from './pages/AgendaVencimentosPage';
import ImportacaoPage from './pages/ImportacaoPage';
import PainelInvestidorPage from './pages/PainelInvestidorPage';
import CriseGerenciamentoPage from './pages/CriseGerenciamentoPage';
import CrisePagamentosPage from './pages/CrisePagamentosPage';
import CriseEncargosPage from './pages/CriseEncargosPage';
import CriseParcelamentosPage from './pages/CriseParcelamentosPage';
import CriseAnuidadesPage from './pages/CriseAnuidadesPage';
import CriseNegociacoesAgregadasPage from './pages/CriseNegociacoesAgregadasPage';
import UsuariosPage from './pages/UsuariosPage';
import CentrosCustoPage from './pages/CentrosCustoPage';
import PlanoContasPage from './pages/PlanoContasPage';
import ContasAPagarPage from './pages/ContasAPagarPage';
import ChamadosPage from './pages/ChamadosPage';
import ChamadoDetalhePage from './pages/ChamadoDetalhePage';
import { colors, API } from './utils/theme';
import { Lock } from 'lucide-react';

// Interceptar fetch para:
//  1. Injetar Authorization automaticamente (se já não vier nos headers)
//  2. Injetar titular_id automaticamente em GETs (pra perfil Titular)
//  3. Detectar 401 com mensagem de token expirado e forçar logout
const originalFetch = window.fetch;
window.fetch = async function(url, options) {
  if (typeof url === 'string' && url.includes('/api/') && !url.includes('/api/auth/')) {
    try {
      // 1. Injetar Authorization header se houver token e o request ainda não tiver
      const token = localStorage.getItem('xm_token');
      if (token) {
        const existing = options?.headers || {};
        const hasAuth = (() => {
          if (existing instanceof Headers) return existing.has('Authorization');
          if (Array.isArray(existing)) return existing.some(([k]) => /^authorization$/i.test(k));
          return Object.keys(existing).some(k => /^authorization$/i.test(k));
        })();
        if (!hasAuth) {
          // Normaliza pra objeto plain pra adicionar Authorization
          let plainHeaders = {};
          if (existing instanceof Headers) {
            existing.forEach((v, k) => { plainHeaders[k] = v; });
          } else if (Array.isArray(existing)) {
            existing.forEach(([k, v]) => { plainHeaders[k] = v; });
          } else {
            plainHeaders = { ...existing };
          }
          plainHeaders.Authorization = `Bearer ${token}`;
          options = { ...(options || {}), headers: plainHeaders };
        }
      }
      // 2. Injetar titular_id pra GETs (perfil Titular vê só seus dados)
      //
      // EXCETO quem tem OPERAÇÃO: pra ele o padrão é o grupo inteiro, e é o
      // seletor da tela que restringe a um investidor. Este interceptador é
      // global — pega TODA chamada da aplicação —, então enquanto ele forçava
      // titular_id=<o próprio> nada mais adiantava: mudar o apiUrl(), a lista
      // de titulares, o painel. Tudo voltava filtrado num titular só.
      //
      // A restrição não depende disto: o servidor calcula o escopo pelo token
      // (server/lib/escopo-titular.js) e recusa titular de fora da operação.
      const usuario = JSON.parse(localStorage.getItem('xm_usuario') || '{}');
      const temOperacao = Array.isArray(usuario.titulares_visiveis) && usuario.titulares_visiveis.length > 1;
      if (usuario.titular_id && !temOperacao) {
        const urlObj = new URL(url, window.location.origin);
        if (!urlObj.searchParams.has('titular_id') && (!options || !options.method || options.method === 'GET')) {
          urlObj.searchParams.set('titular_id', usuario.titular_id);
          url = urlObj.toString();
        }
      }
    } catch(e) {}
  }
  const response = await originalFetch.call(this, url, options);
  // 401 com mensagem "Token inválido ou expirado" → JWT realmente acabou.
  // Forçamos logout: limpa storage e exibe modal pra relogar. Sem isso o usuário
  // fica clicando e nada funciona (só vê erros). Outros 401s (sem permissão pra
  // rota específica) NÃO acionam logout — apenas a mensagem específica de token.
  if (response.status === 401 && typeof url === 'string' && url.includes('/api/') && !url.includes('/api/auth/')) {
    try {
      const cloned = response.clone();
      const data = await cloned.json().catch(() => null);
      // Só força logout se:
      //  - mensagem indica problema com TOKEN (não "Sem permissão" etc.)
      //  - havia um token no localStorage (evita disparo logo após login,
      //    em chamadas que não passaram o token corretamente)
      //  - é a primeira vez na sessão (mutex __xm_tokenExpiredAlerted)
      const tinhaToken = !!localStorage.getItem('xm_token');
      if (tinhaToken && data?.error && /token inv[áa]lido|token expirado/i.test(data.error)) {
        console.warn('🔑 Token expirado em', url, '— forçando logout.');
        if (!window.__xm_tokenExpiredAlerted) {
          window.__xm_tokenExpiredAlerted = true;
          localStorage.removeItem('xm_token');
          localStorage.removeItem('xm_usuario');
          alert('Sua sessão expirou. Faça login novamente.');
          window.location.href = '/';
        }
      } else if (data?.error && /token n[ãa]o fornecido/i.test(data.error)) {
        // "Token não fornecido" = bug no client (fetch sem Authorization).
        // Não desloga; só loga. O interceptor já injeta o token automaticamente,
        // então isso só acontece em casos raros.
        console.warn('⚠ Request sem token em', url);
      }
    } catch (e) { /* ignore */ }
  }
  return response;
};

// Verifica se o usuário tem acesso a uma página
function temAcesso(usuario, pagina) {
  if (!usuario || !usuario.paginas_permitidas) return false;
  if (usuario.paginas_permitidas === '*') return true;
  return usuario.paginas_permitidas.includes(pagina);
}

function SemPermissaoPage() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 }}>
      <Lock size={48} color={colors.textDim} />
      <h2 style={{ margin: 0, color: colors.text, fontSize: 20 }}>Sem Permissao</h2>
      <p style={{ color: colors.textDim, fontSize: 14, textAlign: 'center', maxWidth: 400 }}>
        Voce nao tem perfil de acesso para esta pagina. Solicite ao administrador que atribua o perfil necessario.
      </p>
    </div>
  );
}

export default function App() {
  // Persiste a página atual (chamado #21): ao recarregar, volta pra mesma tela
  // em vez de cair no dashboard. Usa sessionStorage (por ABA) e não localStorage
  // (chamado #27): senão o reload de uma aba pegava a tela de outra aba, já que
  // o localStorage é compartilhado entre abas. sessionStorage sobrevive ao F5.
  const [page, setPage] = useState(() => sessionStorage.getItem('xm_page') || 'dashboard');
  const [usuario, setUsuario] = useState(null);
  const [token, setToken] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => { sessionStorage.setItem('xm_page', page); }, [page]);

  // Investidor com cadastro pendente (14/09/2026) cai no "Meu cadastro" até a
  // gestão aprovar — é o fluxo de entrada de investidor novo.
  useEffect(() => {
    if (usuario?.cadastro_status && usuario.cadastro_status !== 'APROVADO'
        && (usuario.paginas_permitidas || []).includes('meu-cadastro') && page !== 'meu-cadastro') {
      setPage('meu-cadastro');
    }
  }, [usuario, page]);

  useEffect(() => {
    const savedToken = localStorage.getItem('xm_token');
    const savedUsuario = localStorage.getItem('xm_usuario');
    if (savedToken && savedUsuario) {
      try {
        const usr = JSON.parse(savedUsuario);
        // Se não tem paginas_permitidas, forçar re-login
        if (!usr.paginas_permitidas) {
          localStorage.removeItem('xm_token');
          localStorage.removeItem('xm_usuario');
        } else {
          setToken(savedToken);
          setUsuario(usr);
          // Renova as permissões no servidor (03/09/2026): a lista salva no
          // login envelhece — página nova só aparecia depois de relogar.
          // Só troca o que vem do perfil; titular/operação ficam como estão.
          //
          // E a sessão (08/10/2026): titular, operação e o próprio token.
          // Guardados no login, envelheciam igual — a Valéria seguia vendo só
          // a Jaguar no aparelho dela depois de a operação mudar. O servidor
          // manda `sessao` (o mesmo que o login) e, se o token não bate mais
          // com o cadastro, um token novo.
          fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${savedToken}` } })
            .then(r => {
              if (r.status === 401) {          // usuário desativado ou token inválido
                localStorage.removeItem('xm_token');
                localStorage.removeItem('xm_usuario');
                setUsuario(null);
                setToken(null);
                return null;
              }
              return r.ok ? r.json() : null;
            })
            .then(me => {
              if (!me || !me.paginas_permitidas) return;
              let atual = { ...usr, perfis: me.perfis || usr.perfis, paginas_permitidas: me.paginas_permitidas,
                acesso_crise: me.acesso_crise ?? usr.acesso_crise, acesso_cofre: me.acesso_cofre ?? usr.acesso_cofre,
                acesso_apuracao: me.acesso_apuracao ?? usr.acesso_apuracao,
                cadastro_status: me.cadastro_status !== undefined ? me.cadastro_status : usr.cadastro_status };
              if (me.sessao) {
                atual = { ...atual };
                delete atual.titular_id; delete atual.titular_nome;
                delete atual.operacao; delete atual.titulares_visiveis;
                if (me.sessao.titular_id) {
                  atual.titular_id = me.sessao.titular_id;
                  if (me.sessao.titular_nome) atual.titular_nome = me.sessao.titular_nome;
                }
                if (me.sessao.operacao) atual.operacao = me.sessao.operacao;
                if (Array.isArray(me.sessao.titulares_visiveis)) atual.titulares_visiveis = me.sessao.titulares_visiveis;
              }
              if (me.token) {
                localStorage.setItem('xm_token', me.token);
                setToken(me.token);
              }
              localStorage.setItem('xm_usuario', JSON.stringify(atual));
              setUsuario(atual);
            })
            .catch(() => {});
        }
      } catch (e) {
        localStorage.removeItem('xm_token');
        localStorage.removeItem('xm_usuario');
      }
    }
    setCheckingAuth(false);
  }, []);

  const handleLogin = (usr, tkn) => {
    setUsuario(usr);
    setToken(tkn);
  };

  const handleLogout = () => {
    localStorage.removeItem('xm_token');
    localStorage.removeItem('xm_usuario');
    setUsuario(null);
    setToken(null);
    setPage('dashboard');
  };

  if (checkingAuth) return null;

  // Página pública de redefinição de senha (link do e-mail), independe de login.
  if (window.location.pathname === '/resetar-senha') {
    return <ResetSenhaPage />;
  }

  if (!usuario || !token) {
    return <LoginPage onLogin={handleLogin} />;
  }

  const renderPage = () => {
    // Mapa de página → componente
    const pageMap = {
      'dashboard': <DashboardPage />,
      'titulares': <TitularesPage />,
      'cartoes': <CartoesPage />,
      'bancos': <BancosPage />,
      'transacoes': <TransacoesPage />,
      'conciliacao': <ConciliacaoPage />,
      'consulta': <ConsultaPage />,
      'fechamento-fatura': <FechamentoFaturaPage />,
      'remuneracao': <RemuneracaoPage />,
      'meu-cadastro': <MeuCadastroPage />,
      'meu-painel': <MeuPainelPage />,
      'cofre': <CofrePage />,
      'conciliacao-cc': <ConciliacaoCCPage />,
      'fluxo': <FluxoCaixaPage />,
      'agenda-vencimentos': <AgendaVencimentosPage />,
      'importacao': <ImportacaoPage />,
      'painel-investidor': <PainelInvestidorPage />,
      'crise': <CriseGerenciamentoPage />,
      'crise-pagamentos': <CrisePagamentosPage onNavigate={setPage} />,
      'crise-encargos': <CriseEncargosPage />,
      'crise-parcelamentos': <CriseParcelamentosPage />,
      'crise-anuidades': <CriseAnuidadesPage />,
      'crise-negociacoes-agregadas': <CriseNegociacoesAgregadasPage />,
      'usuarios': <UsuariosPage />,
      'centros-custo': <CentrosCustoPage />,
      'plano-contas': <PlanoContasPage />,
      'contas-a-pagar': <ContasAPagarPage onNavigate={setPage} />,
      'chamados': <ChamadosPage onNavigate={setPage} />,
      'chamado-detalhe': <ChamadoDetalhePage onNavigate={setPage} />,
    };

    if (!temAcesso(usuario, page)) {
      return <SemPermissaoPage />;
    }

    return pageMap[page] || <DashboardPage />;
  };

  return (
    <Layout activePage={page} onNavigate={setPage} usuario={usuario} onLogout={handleLogout}>
      <ErrorBoundary key={page}>
        {renderPage()}
      </ErrorBoundary>
    </Layout>
  );
}
